/**
 * Звук ошибки на экране набора.
 *
 * Красный цвет промах отмечает, но не сообщает о нём: при слепом наборе глаза
 * заняты текстом, а не подсветкой, и ошибку человек замечает через несколько
 * символов — когда исправлять уже поздно. Звук доходит, не требуя взгляда.
 *
 * Тон синтезируется, а не берётся файлом: звуковой файл — это лишний сетевой
 * запрос, разрешение в политике безопасности и лицензия на чужой сигнал ради
 * девяноста миллисекунд писка.
 *
 * Живёт в адаптере, потому что это браузерное окружение: ядро обучения о
 * `AudioContext` знать не должно (ARCHITECTURE §2).
 */

/** Частота тона, Гц: низкий и короткий, чтобы отмечать, а не пугать. */
const FREQUENCY_HZ = 180;

/** Длительность сигнала, с. */
const DURATION_S = 0.09;

/** Громкость на пике, 0..1. Сигнал вспомогательный и заглушать набор не должен. */
const PEAK_GAIN = 0.14;

/** Время нарастания, с: без него начало тона звучит щелчком. */
const ATTACK_S = 0.005;

/** Звук, который экран набора подаёт на промах. */
export interface ErrorSound {
  /** Подаёт сигнал. Ошибка синтеза не всплывает: набор важнее звука. */
  readonly play: () => void;
  /** Освобождает звуковое устройство: экран набора закрыт. */
  readonly close: () => void;
}

/** Звук, которого нет: окружение без Web Audio получает молчание, а не отказ. */
const SILENCE: ErrorSound = { play: () => {}, close: () => {} };

/** Конструктор `AudioContext` этого окружения либо null. */
function audioContextConstructor(): typeof AudioContext | null {
  const global = globalThis as { AudioContext?: typeof AudioContext };
  return typeof global.AudioContext === 'function' ? global.AudioContext : null;
}

/**
 * Создаёт источник звука ошибки.
 *
 * Звуковое устройство открывается при первом промахе, а не при создании: у
 * того, кто не ошибается, оно не открывается вовсе. Браузер до первого
 * действия пользователя держит контекст остановленным — нажатие клавиши
 * действием и является, поэтому остановленный контекст запускается на месте.
 */
export function createErrorSound(): ErrorSound {
  const Constructor = audioContextConstructor();
  if (Constructor === null) return SILENCE;

  let context: AudioContext | null = null;

  const play = (): void => {
    try {
      context ??= new Constructor();
      if (context.state === 'suspended') void context.resume();

      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const startedAt = context.currentTime;

      oscillator.type = 'triangle';
      oscillator.frequency.value = FREQUENCY_HZ;

      // Огибающая: быстрый подъём и спад к нулю. Прямоугольный импульс дал бы
      // щелчки на обоих концах — они слышны отчётливее самого тона.
      gain.gain.setValueAtTime(0, startedAt);
      gain.gain.linearRampToValueAtTime(PEAK_GAIN, startedAt + ATTACK_S);
      gain.gain.exponentialRampToValueAtTime(0.0001, startedAt + DURATION_S);

      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(startedAt);
      oscillator.stop(startedAt + DURATION_S);
    } catch {
      // Звук — вспомогательный сигнал: браузер, отказавший в синтезе, не
      // должен ронять набор. Молчание здесь лучше исключения в пути нажатия.
    }
  };

  const close = (): void => {
    try {
      void context?.close();
    } catch {
      // Закрытие уже закрытого устройства ничем не грозит.
    }
    context = null;
  };

  return { play, close };
}
